import { fundingPolicyTypeDefList } from '@shared';

function isFieldCondition(condition: Condition): condition is FieldCondition {
  return 'field' in condition;
}

function camelToTitle(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

function resolveFundingPolicyTypeLabel(rawValue: PolicyFormSubmissionDataValue): string {
  const numericValue = Number(rawValue);
  if (!isNaN(numericValue)) {
    const match = fundingPolicyTypeDefList.find(def => def.value === numericValue);
    if (match) return match.label;
  }
  return String(rawValue);
}

function formatOperator(operator: ComparisonOperator): string {
  const labels: Record<ComparisonOperator, string> = {
    equals: 'equals',
    notEquals: 'does not equal',
    contains: 'contains',
    notContains: 'does not contain',
    empty: 'is empty',
    notEmpty: 'is not empty',
    in: 'is one of',
    notIn: 'is not one of',
    greaterThan: 'is greater than',
    greaterThanOrEqual: 'is greater than or equal to',
    lessThan: 'is less than',
    lessThanOrEqual: 'is less than or equal to',
  };
  return labels[operator] ?? operator;
}

function resolveOptionLabel(
  question: PolicyFormQuestion,
  rawValue: PolicyFormSubmissionDataValue,
): string {
  if (question.options) {
    const match = question.options.find(opt => opt.id === rawValue);
    if (match) return match.value;
  }
  return String(rawValue);
}

function formatFieldCondition(
  condition: FieldCondition,
  questions: Record<string, PolicyFormQuestion>,
): string {
  const question = questions[condition.field];
  const fieldLabel = question ? `"${question.title}"` : `"${camelToTitle(condition.field)}"`;
  const operator = formatOperator(condition.operator);

  if (condition.operator === 'empty' || condition.operator === 'notEmpty') {
    return `${fieldLabel} ${operator}`;
  }

  const resolveValue = (v: PolicyFormSubmissionDataValue) => {
    if (question) return resolveOptionLabel(question, v);
    return resolveFundingPolicyTypeLabel(v);
  };

  const conditionValue = condition.value;
  const value = Array.isArray(conditionValue)
    ? conditionValue.map(v => `"${resolveValue(v as PolicyFormSubmissionDataValue)}"`).join(', ')
    : `"${resolveValue(conditionValue as PolicyFormSubmissionDataValue)}"`;

  return `${fieldLabel} ${operator} ${value}`;
}

function formatConditionInner(
  condition: Condition,
  questions: Record<string, PolicyFormQuestion>,
  depth: number,
): string {
  if (isFieldCondition(condition)) {
    return formatFieldCondition(condition, questions);
  }

  const logicalCondition = condition as LogicalCondition;
  const joiner = logicalCondition.operator === 'and' ? ' AND ' : ' OR ';
  const parts = logicalCondition.conditions.map(c =>
    formatConditionInner(c, questions, depth + 1),
  );

  if (depth > 0 && parts.length > 1) {
    return `(${parts.join(joiner)})`;
  }

  return parts.join(joiner);
}

export function formatConditionText(
  condition: Condition,
  questions: Record<string, PolicyFormQuestion>,
): string {
  return formatConditionInner(condition, questions, 0);
}

export function formatConditionalBehaviorText(
  conditional: ConditionalBehavior,
  questions: Record<string, PolicyFormQuestion>,
): string {
  const lines: string[] = [];

  if (conditional.visible) {
    lines.push(`Visible when: ${formatConditionText(conditional.visible, questions)}`);
  }
  if (conditional.required) {
    lines.push(`Required when: ${formatConditionText(conditional.required, questions)}`);
  }
  if (conditional.showConditionalText) {
    lines.push(
      `Shows conditional text when: ${formatConditionText(conditional.showConditionalText, questions)}`,
    );
  }

  return lines.join('\n');
}
